import {
  DocumentTextIcon,
  CreditCardIcon,
  HomeIcon,
  TrashIcon,
  CloudArrowUpIcon,
} from "@heroicons/react/24/solid";
import { zodResolver } from "@hookform/resolvers/zod";
import { LocationFormProps } from "data/reduxInterfaces";
import { CompanyJustificationData } from "data/types";
import { useAppSelector } from "hooks/hooks";
import { FC, HTMLAttributes, useEffect, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import NcImage from "shared/NcImage/NcImage";
import OnboardingTour, { stepStyle } from "utils/tours/OnboardingTour";
import { z } from "zod";

const MAX_FILE_SIZE = 1024 * 1024 * 10;
const ACCEPTED_IMAGE_MIME_TYPES = [
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/gif",
  "image/webp",
  "application/pdf",
];

const LocationCompanyImagesDataModern: FC<LocationFormProps & HTMLAttributes<HTMLDivElement>> = ({
  className,
  isLoading,
  handleForm,
  errorLocationForm,
  FormData,
}) => {
  const updatelocationStates = useAppSelector((state) => state.location.locationData);

  const formSchema = z.object({
    companyCertifImg_url:
      updatelocationStates?.companyCertifImg_url === null
        ? z
            .instanceof(File, {
              message: "L'image du certificat d'inscription au registre des entreprises est obligatoire.",
            })
            .refine((file) => file && file.size <= MAX_FILE_SIZE, `La taille maximale d'image est de 10MB.`)
            .refine(
              (file) => file && ACCEPTED_IMAGE_MIME_TYPES.includes(file.type),
              "Le fichier doit être une image au format JPEG, PNG GIF ou PDF."
            )
        : z
            .instanceof(File, {
              message: "L'image du certificat d'inscription au registre des entreprises est optionnel.",
            })
            .optional()
            .refine(
              (file) => !file || (file.size <= MAX_FILE_SIZE && ACCEPTED_IMAGE_MIME_TYPES.includes(file.type)),
              "Le fichier doit être une image au format JPEG, PNG GIF ou PDF."
            ),

    iban_url:
      updatelocationStates?.iban_url === null
        ? z
            .instanceof(File, {
              message: " L'image du RIB est obligatoire.",
            })
            .refine((file) => file && file.size <= MAX_FILE_SIZE, `La taille maximale d'image est de 10MB.`)
            .refine(
              (file) => file && ACCEPTED_IMAGE_MIME_TYPES.includes(file.type),
              "Le fichier doit être une image au format JPEG, PNG GIF ou PDF."
            )
        : z
            .instanceof(File, {
              message: "L'image du RIB est optionnel.",
            })
            .optional()
            .refine(
              (file) => !file || (file.size <= MAX_FILE_SIZE && ACCEPTED_IMAGE_MIME_TYPES.includes(file.type)),
              "Le fichier doit être une image au format JPEG, PNG GIF ou PDF."
            ),

    adresseImg_url: z
      .optional(
        z.instanceof(File, {
          message: "Extrait du justification à domicile est obligatoire.",
        })
      )
      .refine(
        (file) => !file || (file?.size <= MAX_FILE_SIZE && ACCEPTED_IMAGE_MIME_TYPES.includes(file?.type)),
        "Le fichier doit être une image au format JPEG, PNG ou GIF et ne pas dépasser 10MB."
      ),
  });

  const {
    handleSubmit,
    formState: { errors },
    getValues,
    setValue,
    trigger,
  } = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {},
    mode: "onSubmit",
  });

  const onSubmit: SubmitHandler<z.infer<typeof formSchema>> = (data) => {
    FormData(data);
  };

  const steps = [
    {
      selector: ".k-bis",
      content:
        "La copie de votre extrait K-Bis ne doit pas être floutée ni tronquée. Veuillez également vérifier sa date d'expiration.",
      stepStyle,
    },
    {
      selector: ".iban ",
      content: "Copie de votre relevé bancaire IBAN.",
    },
  ];

  useEffect(() => {
    if (handleForm.type === "chekLocationFormFifteenCompany") {
      handleSubmit(onSubmit)();
    }
  }, [handleForm]);

  const [showFullText, setShowFullText] = useState(false);

  const handleToggleText = () => {
    setShowFullText(!showFullText);
  };

  const handleDropImage = (e: React.DragEvent<HTMLDivElement>, fieldName: keyof CompanyJustificationData) => {
    e.preventDefault();
    const droppedFiles = e.dataTransfer.files;
    if (droppedFiles) {
      const file = droppedFiles[0];
      setValue(fieldName, file);
      trigger(fieldName as "iban_url" | "companyCertifImg_url" | "adresseImg_url");
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const removeFile = (fieldName: keyof CompanyJustificationData) => {
    setValue(fieldName, undefined);
    trigger(fieldName as "iban_url" | "companyCertifImg_url" | "adresseImg_url");
  };

  const DocumentUpload = ({
    title,
    description,
    fieldName,
    icon: Icon,
    existingFile,
    currentFile,
    error,
    required = false,
  }: {
    title: string;
    description: string | React.ReactNode;
    fieldName: keyof CompanyJustificationData;
    icon: any;
    existingFile?: string;
    currentFile?: File;
    error?: string;
    required?: boolean;
  }) => (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
      <div className="flex items-center mb-4">
        <Icon className="w-6 h-6 text-eventToBee mr-3" />
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
          {title} {required && <span className="text-red-500">*</span>}
        </h3>
      </div>

      <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">{description}</p>

      {/* Image existante */}
      {!currentFile && existingFile && (
        <div className="mb-4">
          <div className="flex items-center space-x-2 mb-2">
            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
            <span className="text-sm font-medium text-green-700 dark:text-green-300">
              Document déjà téléchargé
            </span>
          </div>
          <div className="relative inline-block">
            <NcImage
              src={process.env.REACT_APP_CLOUD_FRONT + existingFile}
              className="max-w-xs max-h-40 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700"
            />
            <button
              onClick={() => removeFile(fieldName)}
              className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors"
            >
              <TrashIcon className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Image sélectionnée */}
      {currentFile && (
        <div className="mb-4">
          <div className="flex items-center space-x-2 mb-2">
            <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
            <span className="text-sm font-medium text-blue-700 dark:text-blue-300">
              Nouveau document sélectionné
            </span>
          </div>
          <div className="relative inline-block">
            <img
              src={URL.createObjectURL(currentFile)}
              alt="Document sélectionné"
              className="max-w-xs max-h-40 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700"
            />
            <button
              onClick={() => removeFile(fieldName)}
              className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors"
            >
              <TrashIcon className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Zone de téléchargement */}
      <div
        className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-8 text-center hover:border-eventToBee dark:hover:border-eventToBee transition-colors"
        onDrop={(e) => handleDropImage(e, fieldName)}
        onDragOver={handleDragOver}
      >
        <CloudArrowUpIcon className="w-12 h-12 mx-auto text-gray-400 mb-4" />

        <label htmlFor={`${fieldName}_input`} className="cursor-pointer">
          <span className="mt-2 block text-sm font-medium text-eventToBee hover:text-blue-700">
            Cliquez pour télécharger
          </span>
          <span className="mt-1 block text-xs text-gray-500 dark:text-gray-400">
            ou glissez-déposez votre fichier ici
          </span>
          <input
            id={`${fieldName}_input`}
            name={fieldName}
            type="file"
            className="sr-only"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) {
                setValue(fieldName, file);
                trigger(fieldName as "iban_url" | "companyCertifImg_url" | "adresseImg_url");
              }
            }}
            accept="image/jpeg, image/jpg, image/png, image/webp, application/pdf"
          />
        </label>

        <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">PNG, JPG, GIF, PDF jusqu'à 10MB</p>
      </div>

      {error && (
        <div className="mt-3 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <div className="flex items-center space-x-2">
            <svg className="w-4 h-4 text-red-500" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                clipRule="evenodd"
              />
            </svg>
            <span className="text-sm text-red-700 dark:text-red-300">{error}</span>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className={className}>
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">📄 Documents justificatifs</h2>
        <p className="text-gray-600 dark:text-gray-400">
          Téléchargez les documents nécessaires à la validation de votre entreprise
        </p>
        <div className="border-b-2 w-20 border-eventToBee mt-4 mx-auto"></div>
      </div>

      <form className="space-y-6" onSubmit={handleSubmit(onSubmit)} encType="multipart/form-data">
        {/* K-Bis / Certificat d'entreprise */}
        <div className="k-bis">
          <DocumentUpload
            title="Extrait K-Bis ou Certificat d'inscription"
            description="La copie de votre K-Bis ne doit pas être floutée ni tronquée. Vérifiez aussi sa date d'expiration."
            fieldName="companyCertifImg_url"
            icon={DocumentTextIcon}
            existingFile={updatelocationStates?.companyCertifImg_url}
            currentFile={getValues("companyCertifImg_url")}
            error={errors.companyCertifImg_url?.message?.toString()}
            required={updatelocationStates?.companyCertifImg_url === null}
          />
        </div>

        {/* RIB */}
        <div className="iban">
          <DocumentUpload
            title="RIB (Relevé d'Identité Bancaire)"
            description="Copie de votre relevé d'identité bancaire. Ces justificatifs seront traités par notre équipe en toute confidentialité."
            fieldName="iban_url"
            icon={CreditCardIcon}
            existingFile={updatelocationStates?.iban_url}
            currentFile={getValues("iban_url")}
            error={errors.iban_url?.message?.toString()}
            required={updatelocationStates?.iban_url === null}
          />
        </div>

        {/* Justificatif de domicile */}
        <DocumentUpload
          title="Justificatif de domicile"
          description={
            <div className="text-sm text-gray-600 dark:text-gray-400">
              {showFullText ? (
                <>
                  Celui-ci peut être soit une facture d'électricité, de gaz ou d'eau, une facture de téléphone pour
                  une ligne fixe ou mobile, une quittance de loyer ou une attestation d'assurance logement pour
                  l'année en cours. Tous ces documents doivent dater de moins de trois mois.
                  <button
                    type="button"
                    onClick={handleToggleText}
                    className="ml-2 text-eventToBee underline hover:text-blue-700"
                  >
                    voir moins
                  </button>
                </>
              ) : (
                <>
                  Celui-ci peut être soit une facture d'électricité, de gaz ou d'eau, une facture de téléphone pour
                  une ligne fixe ou mobile...
                  <button
                    type="button"
                    onClick={handleToggleText}
                    className="ml-2 text-eventToBee underline hover:text-blue-700"
                  >
                    voir plus
                  </button>
                </>
              )}
            </div>
          }
          fieldName="adresseImg_url"
          icon={HomeIcon}
          existingFile={updatelocationStates?.adresseImg_url}
          currentFile={getValues("adresseImg_url")}
          error={errors.adresseImg_url?.message?.toString()}
        />

        {/* Notice légale */}
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <div className="w-5 h-5 bg-blue-500 rounded-full mt-0.5 flex items-center justify-center flex-shrink-0">
              <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div>
              <p className="text-sm text-blue-900 dark:text-blue-200">
                En enregistrant les informations, vous acceptez le{" "}
                <a
                  href="https://stripe.com/legal/connect-account"
                  className="font-semibold text-eventToBee hover:underline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Stripe Connected Account Agreement
                </a>
              </p>
            </div>
          </div>
        </div>
      </form>

      <OnboardingTour steps={steps} page_name={"CompanyImagesDataForm"}></OnboardingTour>
    </div>
  );
};

export default LocationCompanyImagesDataModern;
